import type { FormEvent, ReactNode } from "react";
import { Alerta, Button, MontoField, type OpcionSelect, Row, Select, Stack, TextField } from "@/components";
import { etiquetaFee, OPCIONES_TIPO, type ErroresGasto, type FormGasto } from "@/lib/gastos";

export interface FormularioGastoProps {
  form: FormGasto;
  cambiar: <K extends keyof FormGasto>(campo: K, valor: FormGasto[K]) => void;
  errores: ErroresGasto;
  opcionesProveedor: OpcionSelect[];
  /** Alta rápida: lo escrito que no está en la agenda se ofrece como "Agregar proveedor «…»". */
  onCrearProveedor: (nombre: string) => void;
  opcionesRubro: OpcionSelect[];
  /** Presupuesto aceptado, pago de su plan e ítem (`useVinculosFactura`); vacíos, no se muestran. */
  opcionesPresupuesto?: OpcionSelect[];
  opcionesCuota?: OpcionSelect[];
  opcionesItem?: OpcionSelect[];
  feePrevisto: string | null;
  /** Sólo en el alta: el archivo del comprobante (en la edición se sube desde la ficha). */
  comprobante?: ReactNode;
  feePorcentaje: string | null;
  onSubmit: (e: FormEvent) => void;
  onCancelar: () => void;
  guardando: boolean;
  error: string | null;
}

/**
 * Campos del formulario de gasto (alta y edición). Sin `<Dialog>` ni acciones fuera del "Guardar"/"Cancelar":
 * el listado (`GastosTab`) lo usa dentro del diálogo de alta y la ficha (`GastoTab`) lo reusa para editar el gasto.
 */
export function FormularioGasto({
  form,
  cambiar,
  errores,
  opcionesProveedor,
  onCrearProveedor,
  opcionesRubro,
  opcionesPresupuesto = [],
  opcionesCuota = [],
  opcionesItem = [],
  feePrevisto,
  comprobante,
  feePorcentaje,
  onSubmit,
  onCancelar,
  guardando,
  error,
}: FormularioGastoProps) {
  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack espacio={4}>
        <TextField etiqueta="Fecha" type="date" value={form.fecha} onChange={(e) => cambiar("fecha", e.target.value)} error={errores.fecha} />
        <Select etiqueta="Proveedor" opciones={opcionesProveedor} valor={form.proveedor_id || undefined} onCambio={(v) => cambiar("proveedor_id", v)} placeholder="Elegí un proveedor" error={errores.proveedor_id} onCrear={onCrearProveedor} textoCrear={(n) => `Agregar proveedor «${n}»`} />
        {opcionesPresupuesto.length > 0 && (
          <Select etiqueta="Presupuesto aceptado" opciones={opcionesPresupuesto} valor={form.cotizacion_id || undefined} onCambio={(v) => cambiar("cotizacion_id", v)} placeholder="Opcional" />
        )}
        {opcionesCuota.length > 0 && (
          <Select etiqueta="Pago del plan" opciones={opcionesCuota} valor={form.cuota_id || undefined} onCambio={(v) => cambiar("cuota_id", v)} placeholder="Opcional" />
        )}
        <Select etiqueta="Rubro" opciones={opcionesRubro} valor={form.rubro_id || undefined} onCambio={(v) => cambiar("rubro_id", v)} placeholder="Elegí un rubro" error={errores.rubro_id} />
        {opcionesItem.length > 0 && (
          <Select etiqueta="Ítem" opciones={opcionesItem} valor={form.item_id || undefined} onCambio={(v) => cambiar("item_id", v)} placeholder="Opcional" />
        )}
        <Select etiqueta="Tipo" opciones={OPCIONES_TIPO} valor={form.tipo} onCambio={(v) => cambiar("tipo", v as "A" | "B")} className="w-28" />
        <TextField etiqueta="Descripción" value={form.descripcion} onChange={(e) => cambiar("descripcion", e.target.value)} error={errores.descripcion} />
        <TextField etiqueta="Nro de comprobante" ayuda="Opcional" value={form.comprobante_nro} onChange={(e) => cambiar("comprobante_nro", e.target.value)} error={errores.comprobante_nro} />
        {comprobante}
        <MontoField etiqueta="Monto" valor={form.monto} onCambio={(v) => cambiar("monto", v)} moneda={form.moneda} onCambioMoneda={(m) => cambiar("moneda", m)} error={errores.monto} />
        {form.moneda === "ARS" && (
          <TextField etiqueta="Tipo de cambio (ARS por USD)" inputMode="decimal" ayuda="Se sugiere el último usado" value={form.tipo_cambio} onChange={(e) => cambiar("tipo_cambio", e.target.value)} error={errores.tipo_cambio} />
        )}
        <Row espacio={2} alinear="centro">
          <Button variante={form.lleva_fee ? "primario" : "secundario"} tamanio="sm" aria-pressed={form.lleva_fee} onClick={() => cambiar("lleva_fee", !form.lleva_fee)}>
            {form.lleva_fee ? "Con fee de administración" : "Sin fee de administración"}
          </Button>
          {feePrevisto && feePorcentaje && <span className="text-chico text-texto-suave">{etiquetaFee(feePrevisto, feePorcentaje, form.moneda)}</span>}
        </Row>
        <Alerta texto={error} />
        <Row justificar="fin" espacio={2}>
          <Button variante="secundario" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit" cargando={guardando}>
            Guardar
          </Button>
        </Row>
      </Stack>
    </form>
  );
}
